// React
import { useRef, useLayoutEffect, useState, type FC } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

// Styles
import '../styles/Quiz.css';

// Components
import MissionHeader from '../components/MissionHeader';
import MainButton from '../components/MainButton';

// Contexts
import { useTimer } from '../contextes/useTimer';

// Data
import { quizData as mission1QuizData } from '../assets/data/mission1/quizData';
import { finalQuizData as mission1FinalQuizData } from '../assets/data/mission1/finalQuizData';
import { contentData } from '../assets/data/contentData';

// GSAP
import gsap from 'gsap';
import PageTransition from '../components/PageTransition';
import Debrief from '../components/Debrief';

const SELECTORS = {
    question: '.quiz-question',
    optionWrap: '.quiz-button-wrap',
    options: '.options-container .option-button, .options-container .quiz-button-shadow',
    imageQuestion: '.image-bebe-quiz',
    imageDebriefFail: '.image-bebe-quiz-fail',
    imageDebriefFinal: '.image-bebe-quizDebrief',
    debriefTitle: '.debrief-titre',
    debriefText: '.debrief-text',
    nextButtonWrap: '.next-question-button-wrap',
};

type QuizQuestion = {
    question: string;
    options: string[];
    answer: string;
    debrief: string;
    finalText?: string;
    illustrationQuestion: string;
    illustrationDebrief: string;
};

// carte -> nom de dataset -> tableau de questions
const quizDataByMission: Record<number, Record<string, QuizQuestion[]>> = {
    1: {
        quizData: mission1QuizData,
        finalQuizData: mission1FinalQuizData,
    },
    // plus tard :
    // 2: { quizData: mission2QuizData, finalQuizData: mission2FinalQuizData },
};

const Quiz: FC = () => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
    const [isCorrectAnswer, setIsCorrectAnswer] = useState(true);
    const [isSuivantClick, setIsSuivantClick] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const navigate = useNavigate();
    const { stopTimer } = useTimer();

    let isFinalQuiz = false;

    const el = useRef<HTMLDivElement>(null);
    const EASE_IN = 'back.out(1.7)';

    // --- Hooks de routing (toujours en haut, avant tout return) ---
    const { missionNumber } = useParams<{ missionNumber: string }>();
    const location = useLocation() as {
        state?: { cardNumber: number };
    };

    // ---------- RÉSOLUTIONS DE DONNÉES SANS HOOKS ----------
    let errorMessage: string | null = null;
    let quizDataToUse: QuizQuestion[] | null = null;
    let isLastQuestion = false;

    const mission = contentData.missions.find((m) => m.id === Number(missionNumber));

    if (!mission) {
        errorMessage = 'Mission introuvable';
    } else {
        const cardNumber = location.state?.cardNumber;

        if (cardNumber == null) {
            errorMessage = 'Numéro de carte manquant.';
        } else {
            const quizConfig = mission.dataQuiz?.[cardNumber];

            if (!quizConfig) {
                errorMessage = 'Pas de quiz configuré pour cette carte.';
            } else {
                const dataKey = quizConfig.key; // 'quizData' ou 'finalQuizData'
                const missionQuizMap = quizDataByMission[mission.id];
                isFinalQuiz = quizConfig.isFinalQuiz;

                if (!missionQuizMap) {
                    errorMessage = 'Aucun quiz défini pour cette mission.';
                } else {
                    const data = missionQuizMap[dataKey];
                    if (!data) {
                        errorMessage = `Dataset introuvable pour cette clé : ${dataKey}`;
                        console.error(errorMessage);
                    } else {
                        quizDataToUse = data;
                        isLastQuestion = currentQuestionIndex === data.length - 1;
                    }
                }
            }
        }
    }

    /* ----------------- Helpers animations ----------------- */

    const questionAndImageIn = () => {
        return gsap
            .timeline()
            .fromTo(
                SELECTORS.question,
                { opacity: 0, y: -40 },
                { opacity: 1, y: 0, duration: 0.4, ease: EASE_IN },
            )
            .fromTo(
                SELECTORS.optionWrap,
                { opacity: 0, y: -40 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    ease: 'elastic.out(1.2,0.75)',
                    stagger: 0.2,
                },
                '-=0.2',
            )
            .fromTo(
                SELECTORS.imageQuestion,
                { opacity: 0, y: 40 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    ease: EASE_IN,
                    onComplete: () => {
                        setIsAnimating(false);
                    },
                },
                '-=0.3',
            );
    };

    const questionAndImageOut = () => {
        return gsap
            .timeline()
            .to(SELECTORS.question, {
                opacity: 0,
                y: -20,
                duration: 0.4,
                ease: 'power2.out',
                delay: 0.1,
            })
            .to(
                SELECTORS.imageQuestion,
                {
                    opacity: 0,
                    y: 40,
                    duration: 0.4,
                    ease: 'power2.out',
                },
                '-=0.3',
            );
    };

    const animateOptionsIn = () => {
        return gsap.fromTo(
            SELECTORS.options,
            { opacity: 0, y: 40 },
            {
                opacity: 1,
                y: 0,
                duration: 0.4,
                ease: EASE_IN,
                stagger: 0.2,
                delay: 0.2,
                onComplete: () => {
                    setIsAnimating(false);
                },
            },
        );
    };

    const animateOptionsOut = () => {
        return gsap.to(SELECTORS.options, {
            opacity: 0,
            y: -40,
            duration: 0.4,
            ease: EASE_IN,
            stagger: 0.2,
            delay: 0.1,
        });
    };

    const animateDebriefIn = () => {
        const target = [
            SELECTORS.debriefTitle,
            SELECTORS.debriefText,
            SELECTORS.imageDebriefFail,
            SELECTORS.nextButtonWrap,
            SELECTORS.imageDebriefFinal,
        ];

        return gsap.fromTo(
            target,
            { opacity: 0, y: -40 },
            {
                opacity: 1,
                y: 0,
                duration: 0.4,
                ease: EASE_IN,
                stagger: 0.2,
                onComplete: () => {
                    // 🔥 fin de l’anim de debrief → on réactive les boutons
                    setIsAnimating(false);
                },
            },
        );
    };

    const animateDebriefOut = () => {
        const target = [
            SELECTORS.debriefTitle,
            SELECTORS.debriefText,
            SELECTORS.imageDebriefFail,
            SELECTORS.nextButtonWrap,
        ];

        return gsap.to(target, {
            opacity: 0,
            y: -20,
            duration: 0.3,
            ease: 'power2.out',
            stagger: 0.15,
        });
    };

    /* ----------------- Animation initiale ----------------- */

    useLayoutEffect(() => {
        if (!el.current) return;

        const ctx = gsap.context(() => {
            questionAndImageIn();
        }, el);

        return () => ctx.revert();
    }, []);

    /* ------ Transitions question -> debrief / debrief -> question ------ */

    useLayoutEffect(() => {
        if (!el.current) return;

        const ctx = gsap.context(() => {
            // première fois : anim d'entrée
            if (!isAnswerSubmitted) {
                setIsAnimating(true);
                questionAndImageIn();
            }

            // quand la réponse vient d’être soumise : question/options OUT → debrief IN
            if (isAnswerSubmitted) {
                setIsAnimating(true);
                const tl = gsap.timeline();
                tl.add(questionAndImageOut())
                    .add(animateOptionsOut(), '<')
                    .add(animateDebriefIn(), '<');
            }
        }, el);

        return () => ctx.revert();
    }, [isAnswerSubmitted]);

    /* ------ Handles functions ------ */
    const handleNextQuestion = () => {
        setIsAnimating(true);
        const tl = gsap.timeline({
            onComplete: () => {
                // une fois le debrief complètement sorti
                setSelectedOption(null);
                setIsAnswerSubmitted(false);
                setIsCorrectAnswer(true);
                setCurrentQuestionIndex((prev) => prev + 1);

                // et on fait entrer la nouvelle question
                questionAndImageIn();
                animateOptionsIn();
                setIsAnimating(false);
            },
        });

        tl.add(animateDebriefOut());
    };

    const handleRetry = () => {
        setIsAnimating(true);
        const tl = gsap.timeline({
            onComplete: () => {
                // reset pour réessayer la même question
                // setIsQuestionRetry(false);
                setIsCorrectAnswer(true);
                setSelectedOption(null);
                setIsAnswerSubmitted(false);

                // on réaffiche question + options
                questionAndImageIn();
                animateOptionsIn();
                setIsAnimating(false);
            },
        });

        tl.add(animateDebriefOut());
    };

    const handlSuivantClick = () => {
        // Logique pour le bouton Suivant du quiz final
        console.log('Quiz final terminé. Logique pour continuer la mission ici.');
        setIsSuivantClick(true);
        stopTimer();

        // Par exemple, rediriger vers une autre page ou afficher un message de fin.
    };

    /* ------ Render Button Function ------ */
    const renderDebriefButton = () => {
        // Cas : bonne réponse
        if (isCorrectAnswer) {
            // Pas la dernière question → "Question Suivante"
            if (!isLastQuestion) {
                return (
                    <MainButton
                        text='Question Suivante'
                        onClick={handleNextQuestion}
                        disabled={isAnimating}
                    />
                );
            }

            // Dernière question et quiz final → "Suivant"
            if (isFinalQuiz) {
                return (
                    <MainButton
                        text='Suivant'
                        onClick={handlSuivantClick}
                        disabled={isAnimating}
                    />
                );
            }

            // Dernière question mais pas un quiz final → pas de bouton
            return null;
        }

        // Cas : mauvaise réponse → "Réessayer"
        return (
            <MainButton
                text='Réessayer'
                backgroundColor='secondary'
                onClick={handleRetry}
                disabled={isAnimating}
            />
        );
    };

    if (errorMessage || !quizDataToUse) {
        return <div>{errorMessage ?? 'Erreur de chargement du quiz.'}</div>;
    }

    return (
        <div
            className={`quiz-page`}
            ref={el}
        >
            {!isAnswerSubmitted && (
                <MissionHeader
                    showBackButton={false}
                    showCloseButton={true}
                    showSettingsButton={false}
                    closeButtonVariant='tertiary'
                    logoVariant='default'
                />
            )}
            <div
                className='quiz-container'
                style={{
                    height: isCorrectAnswer ? 'fit-content' : '100%',
                    justifyContent: isCorrectAnswer ? 'flex-start' : 'space-between',
                }}
            >
                <div className='quiz-question'>
                    <p>
                        {currentQuestionIndex + 1} / {quizDataToUse?.length ?? 0}
                    </p>
                    <h2>{quizDataToUse[currentQuestionIndex].question}</h2>
                </div>
                <div className='options-container'>
                    {quizDataToUse[currentQuestionIndex].options.map((option) => (
                        <div
                            className='quiz-button-wrap'
                            key={option}
                        >
                            <button
                                className={`option-button ${
                                    selectedOption === option ? 'selected' : ''
                                }`}
                                onClick={() => {
                                    if (!isAnswerSubmitted) {
                                        setSelectedOption(option);
                                        setIsAnswerSubmitted(true);

                                        const isCorrect =
                                            option === quizDataToUse[currentQuestionIndex].answer;

                                        setIsCorrectAnswer(isCorrect);
                                    }
                                }}
                                disabled={isAnimating || isAnswerSubmitted}
                            >
                                {option}
                            </button>
                            <div className='quiz-button-shadow'></div>
                        </div>
                    ))}
                    <img
                        src={`./images/quiz/${
                            quizDataToUse[currentQuestionIndex].illustrationQuestion || 'bebe-quiz'
                        }.png`}
                        alt='Illustration bébé quiz'
                        className='image-bebe-quiz'
                    />
                </div>
                {isAnswerSubmitted && (
                    <>
                        <Debrief
                            isCorrectAnswer={isCorrectAnswer}
                            textDebrief={quizDataToUse[currentQuestionIndex].debrief}
                            illustrationDebrief={
                                quizDataToUse[currentQuestionIndex].illustrationDebrief
                            }
                            handleButtonClick={() => {
                                animateDebriefOut();
                                handleRetry();
                            }}
                        />

                        {isCorrectAnswer && (
                            <div
                                className='next-question-button-wrap'
                                style={{
                                    marginTop: isCorrectAnswer ? '5rem' : '1rem',
                                    marginBottom: '1rem',
                                    marginInline: 'auto',
                                    zIndex: 100,
                                }}
                            >
                                {renderDebriefButton()}
                            </div>
                        )}
                    </>
                )}
                {isSuivantClick && (
                    <div className='final-debrief'>
                        <PageTransition>
                            <div className='final-debrief'>
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            quizDataToUse[currentQuestionIndex].finalText?.replace(
                                                /\n/g,
                                                '<br />',
                                            ) || '',
                                    }}
                                />
                                <img
                                    src={`./images/quiz/${
                                        quizDataToUse[currentQuestionIndex].illustrationDebrief ||
                                        'bebe-quiz'
                                    }.png`}
                                    alt='Illustration bébé quiz'
                                    className='image-bebe-quizDebriefFinal'
                                />
                                <MainButton
                                    text='Finir le chapitre'
                                    backgroundColor='secondary'
                                    onClick={() => {
                                        navigate(`/mission/${missionNumber}/endgame`, {
                                            replace: true,
                                            state: { hasWon: true },
                                        });
                                    }}
                                    disabled={isAnimating}
                                />
                            </div>
                        </PageTransition>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Quiz;
